/**
 * BrioDropzone -- drag-and-drop file upload area.
 *
 * The custom element IS the dropzone. Click anywhere inside it to
 * open the file picker, or drag and drop files onto it. Files are
 * validated against the input's accept attribute and -- when valid
 * -- assigned to the input so the surrounding <form> submits them
 * normally.
 *
 * Two integration paths, both wired automatically:
 *
 *   1. Native form submission. Accepted files are written to
 *      input.files and a bubbling native "change" event fires on
 *      the input. A standard <form> submission picks them up; htmx,
 *      datastar, alpine, etc. that bind to form-element changes
 *      also see the event.
 *
 *   2. Custom event. brio:dropzone-change fires on the
 *      <brio-dropzone> with detail: { files } (a FileList). Bubbles.
 *      Use this when the consumer drives the upload directly (an
 *      AJAX call, an SSE start, an instrumentation event) instead
 *      of -- or in addition to -- form submission.
 *
 * Single code path: both the drop handler and the file picker funnel
 * through the input's "change" event, so accept-validation, state
 * painting, and event emission run in one place. The drop handler
 * just primes input.files and fires the change event manually
 * (assignment doesn't fire it automatically); the picker fires it
 * naturally when the user confirms a selection.
 *
 * Usage:
 *   <brio-dropzone class="dropzone">
 *       <input type="file" id="attachment" accept=".png,.jpg,.pdf">
 *       <svg aria-hidden="true">...</svg>
 *       <span data-prompt>Drag and drop a file here, or click to browse</span>
 *       <span class="text-subtle">PNG, JPG, or PDF up to 10 MB</span>
 *   </brio-dropzone>
 *
 *   document.querySelector("brio-dropzone")
 *           .addEventListener("brio:dropzone-change", (e) => {
 *       fetch("/upload", { method: "POST", body: makeFormData(e.detail.files) });
 *   });
 *
 * Programmatic:
 *   import { getFiles } from "./brio-dropzone.js";
 *   const files = getFiles(document.querySelector("brio-dropzone"));
 *
 * State classes (set by this element, styled by brio-dropzone.css):
 *   .is-dragover   A drag operation is currently over the element.
 *                  Tracked with a depth counter because dragenter /
 *                  dragleave fire on every child as the cursor moves
 *                  across the dropzone -- a naive add/remove flickers
 *                  whenever the cursor crosses an inner element.
 *   .is-accepted   Files were validated and adopted. Persists.
 *   .is-rejected   Files failed accept-pattern validation. Persists
 *                  until the next valid acceptance (which swaps the
 *                  class) or the consumer clears it manually.
 *
 * Slots / attributes:
 *   class="dropzone"        Required for styling (the brio class-as-
 *                           opt-in convention). The element provides
 *                           the JS regardless.
 *   <input type="file">     Required child. Receives the files.
 *                           accept="..." filters drops the same way
 *                           the browser filters the file picker.
 *   [data-prompt]           Optional child whose textContent gets
 *                           rewritten on accept (with the file name
 *                           or count) or on reject (with an error
 *                           message). The original prompt text is
 *                           captured on the first rejection so it
 *                           can be restored if needed.
 */
import { BrioBase } from '../brio-base.js';

/**
 * Match a file against an accept-attribute pattern.
 * Handles extensions (.png), MIME types (image/png), and wildcards
 * (image/*). Mirrors what the browser's file picker does so that
 * dropped files and picked files are validated identically.
 */
function fileMatchesAccept(file, accept) {
    if (!accept) return true;
    const types = accept.split(',').map((s) => s.trim().toLowerCase());
    const name = file.name.toLowerCase();
    const mime = file.type.toLowerCase();
    return types.some((type) => {
        if (type.startsWith('.')) return name.endsWith(type);
        if (type.endsWith('/*')) return mime.startsWith(type.slice(0, -1));
        return mime === type;
    });
}

function formatFileNames(files) {
    if (files.length === 1) return files[0].name;
    return `${files.length} files selected`;
}

/**
 * Get the current FileList from a dropzone's <input type="file">.
 * Returns null if the dropzone has no file input.
 */
export function getFiles(zone) {
    const input = zone.querySelector('input[type="file"]');
    return input ? input.files : null;
}

class BrioDropzone extends BrioBase {
    static tag = 'brio-dropzone';
    static {
        customElements.define(BrioDropzone.tag, BrioDropzone);
    }

    // Stored on the instance so disconnectedCallback can remove the
    // exact handler references it added.
    #input;
    #prompt;
    #depth = 0;
    #originalPrompt = null;
    #onClick;
    #onDragover;
    #onDragenter;
    #onDragleave;
    #onDrop;
    #onChange;

    connectedCallback() {
        this.#input = this.$('input[type="file"]');
        this.#prompt = this.$('[data-prompt]');
        if (!this.#input) return;

        // Click anywhere inside the dropzone (except on the input
        // itself, which already handles its own click) opens the
        // native file picker. The picker dialog requires user-gesture
        // context; the click handler runs in that context, so we're
        // fine to call input.click() programmatically.
        this.#onClick = (e) => {
            if (e.target !== this.#input) this.#input.click();
        };

        // dragover must preventDefault so the browser knows this is a
        // valid drop target (the default behavior is "no drop").
        this.#onDragover = (e) => {
            e.preventDefault();
        };

        // Depth counter: dragenter/dragleave fire on every descendant
        // the cursor crosses, so a naive add/remove flickers as the
        // cursor moves over an inner span or icon. Counting net
        // entries (enters - leaves) tells us when the cursor truly
        // leaves the dropzone.
        this.#onDragenter = () => {
            this.#depth++;
            if (this.#depth === 1) this.classList.add('is-dragover');
        };

        this.#onDragleave = () => {
            this.#depth--;
            if (this.#depth <= 0) {
                this.#depth = 0;
                this.classList.remove('is-dragover');
            }
        };

        // Drop primes the input with the dropped files and fires a
        // bubbling native "change" event, which our #onChange handler
        // catches and processes -- single code path for drop and
        // picker. Without the manual dispatch, assigning input.files
        // wouldn't fire change (assignment is silent).
        this.#onDrop = (e) => {
            e.preventDefault();
            this.#depth = 0;
            this.classList.remove('is-dragover');

            const files = e.dataTransfer.files;
            if (files.length === 0) return;

            this.#input.files = files;
            this.#input.dispatchEvent(new Event('change', { bubbles: true }));
        };

        // Single funnel for both drop and picker. Validates against
        // the input's accept attribute (covers the picker-bypass case
        // where the user chose "All Files" in the system dialog and
        // the OS didn't filter), paints state, and emits the custom
        // event. Listening on the host for change (it bubbles from
        // the input) instead of on the input directly means the
        // listener cleans up with the host.
        this.#onChange = (e) => {
            if (e.target !== this.#input) return;
            const files = this.#input.files;
            if (files.length === 0) return;

            const accept = this.#input.getAttribute('accept');
            if (accept) {
                const rejected = Array.from(files).filter((f) => !fileMatchesAccept(f, accept));
                if (rejected.length > 0) {
                    this.#paintRejected(rejected);
                    return;
                }
            }

            this.#paintAccepted(files);
        };

        this.addEventListener('click', this.#onClick);
        this.addEventListener('dragover', this.#onDragover);
        this.addEventListener('dragenter', this.#onDragenter);
        this.addEventListener('dragleave', this.#onDragleave);
        this.addEventListener('drop', this.#onDrop);
        this.addEventListener('change', this.#onChange);
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.removeEventListener('dragover', this.#onDragover);
        this.removeEventListener('dragenter', this.#onDragenter);
        this.removeEventListener('dragleave', this.#onDragleave);
        this.removeEventListener('drop', this.#onDrop);
        this.removeEventListener('change', this.#onChange);
    }

    #paintAccepted(files) {
        this.classList.remove('is-rejected');
        this.classList.add('is-accepted');
        if (this.#prompt) this.#prompt.textContent = formatFileNames(files);
        this.emit('dropzone-change', { files });
    }

    #paintRejected(rejected) {
        this.classList.remove('is-accepted');
        this.classList.add('is-rejected');
        if (this.#prompt) {
            // Capture the original prompt the first time we reject so
            // the consumer can restore it if they clear .is-rejected.
            if (this.#originalPrompt === null) {
                this.#originalPrompt = this.#prompt.textContent;
            }
            const names = rejected.length === 1 ? rejected[0].name : `${rejected.length} files`;
            this.#prompt.textContent = `File type not accepted: ${names}`;
        }
    }
}
